import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of as observableOf } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { actionAndETagUpdate$ } from '®shared/container-structure/edit/queue';
import { getETagFromError } from '®shared/tarl-v3-jsonapi';

import { HierarchyAndStudentNumbersService } from '../common';

import { extractInitialHierarchyAndStudentNumbersForUI } from './nodes-editor-modal-utils';
import {
  NodesEditorActionTypes,
  NodesEditorHideModalAction,
  NodesEditorNewListHideModalAction,
  NodesEditorNodesLoadedAction,
  NodesEditorNodesLoadingFailedAction,
  NodesEditorNodesRequestedAction,
  NodesEditorSaveNodesAction,
  NodesEditorSaveNodesFailedAction,
  NodesEditorSaveNodesSucceededAction,
  NodesEditorShowModalAction,
} from './nodes-editor-modal.actions';

@Injectable()
export class NodesEditorModalEffects {
  showHierarchyAndStudentNumbersModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NodesEditorActionTypes.NODES_EDITOR_SHOW_MODAL),
      map(
        (action: NodesEditorShowModalAction) =>
          new NodesEditorNodesRequestedAction(action.payload),
      ),
    ),
  );

  loadCurrentHierarchyAndStudentNumbers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NodesEditorActionTypes.NODES_EDITOR_NODES_REQUESTED),
      switchMap((action: NodesEditorNodesRequestedAction) =>
        this.hierarchyAndStudentNumbersService
          .getRawHierarchyAndStudentNumbersForList(action.payload.listId)
          .pipe(
            map(extractInitialHierarchyAndStudentNumbersForUI),
            map(
              hierarchyAndStudentNumbers =>
                new NodesEditorNodesLoadedAction({
                  ...action.payload,
                  hierarchyAndStudentNumbers,
                }),
            ),
            catchError(error =>
              observableOf(
                new NodesEditorNodesLoadingFailedAction({
                  ...action.payload,
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  saveHierarchyAndStudentNumbers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NodesEditorActionTypes.NODES_EDITOR_SAVE_NODES),
      switchMap((action: NodesEditorSaveNodesAction) => {
        const { listId, isDraft, hierarchyAndStudentNumbers } = action.payload;
        return this.hierarchyAndStudentNumbersService
          .saveHierarchyAndStudentNumbersForList(
            listId,
            isDraft,
            hierarchyAndStudentNumbers,
          )
          .pipe(
            switchMap(etag =>
              actionAndETagUpdate$(
                new NodesEditorSaveNodesSucceededAction(action.payload),
                etag,
              ),
            ),
            catchError(error =>
              actionAndETagUpdate$(
                new NodesEditorSaveNodesFailedAction({
                  ...action.payload,
                  error,
                }),
                getETagFromError(error),
              ),
            ),
          );
      }),
    ),
  );

  hideModalOnPersistHierarchyAndStudentNumbers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NodesEditorActionTypes.NODES_EDITOR_NEW_LIST_PERSIST_NODES),
      map(() => new NodesEditorNewListHideModalAction()),
    ),
  );

  hideModalOnSuccessfulSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NodesEditorActionTypes.NODES_EDITOR_SAVE_NODES_SUCCEEDED),
      map(() => new NodesEditorHideModalAction()),
    ),
  );

  constructor(
    private actions$: Actions,
    private hierarchyAndStudentNumbersService: HierarchyAndStudentNumbersService,
  ) {}
}
